<script lang="ts" setup>
import { computed, inject, ref } from 'vue';
import { useEventCommonStoreRef, useGuiStore } from '@/stores';
import { StationAlias } from '@/objects/generated';
import { BaseObjListView, ButtonGroup, ToolbarComp } from '@/components';
import ContentComp from '@/components/layout/ContentComp.vue';
import { useHelper, useTaskLoader, useDialog } from '@/composeables';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import type BaseObjListViewCompExpose from '@/objects/BaseObjListViewCompExpose';
import { type IGridDetailExposed } from '@/components/Grid/Classes/IGridDetailExposed';
import { DropdownItemButton } from '@/objects';
import { createStationAlias } from '../Station/stationHelpers';
import { arrayGroupbyToMap, propertyof } from '@/utilities';
import type { Ref } from 'vue';
import type { ListCompState } from '@/components/List/ListCompState';

/**
 * Array of StationAlias we are editing
 */
const data = defineModel<StationAlias[]>({ required: true });

const props = withDefaults(
    defineProps<{
        /**
         * The ID of the station we are showing the relay team for
         */
        id: number;

        /**
         * The custom columns for the grid
         * These are columns that are not in the GUI definition
         * @default []
         */
        customColumns?:
            | Array<GridColumnOptions<StationAlias>>
            | ((columns: Array<GridColumnOptions<StationAlias>>) => Array<GridColumnOptions<StationAlias>>);

        /**
         * If this should be shown in a grid as a master detail view.
         * This will remove the toolbar and the status bar and shortcut keys.
         * This mean that saving etc needs to be handled by the parent component
         * @default false
         */
        gridDetailView?: boolean;
    }>(),
    {
        gridDetailView: false,
    },
);

const { AddLoader, LoadState, ClearLoaders } = useTaskLoader();

const { Event } = useEventCommonStoreRef();

const list = ref<BaseObjListViewCompExpose<StationAlias> | null>(null);

/**
 * Callback for when the view get visible
 * @param state
 */
const isVisible = (isVisible: boolean) => {};

/**
 * Refresh the list
 */
const savedList = async (ids?: number[]) => {
    await refreshList(ids);
};

/**
 * Refresh the list
 */
const refreshList = async (ids?: number[]) => {
    ClearLoaders();
    AddLoader(async () => {}, 'relaycontestants.loading');
};

/**
 * Callback when a new item is added to the grid.
 * We need to set the stationsId of the current stationrole
 * @param item
 */
const itemAdded = (item: StationAlias) => {
    if (Event.value == null) {
        return;
    }
    const defaultAlias = createStationAlias(Event.value!);

    item.StationID = props.id;
    item.ReadsChip = defaultAlias.ReadsChip;
    item.ReadsBib = defaultAlias.ReadsBib;
    item.ActiveFrom = defaultAlias.ActiveFrom;
    item.ActiveTo = defaultAlias.ActiveTo;

    // TODO: We want to set the focus to the Alias field name

    // Since this is not added to the list yet, we need to wait a bit
    setTimeout(() => {
        list.value?.startEditing(propertyof<StationAlias>('Alias'));
    }, 100);
};

/**
 * If we press CTRL+Enter we should add a new item to the grid
 * @param event
 */
const onCellKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
        list.value?.addItem();
        return useHelper().stopPropagation(event);
    }
};

/**
 * Helper property to check if the data in the grid has changed
 */
const dataHasChanged = ref(false);

/**
 * Helper property to check if the data in the grid is valid
 */
const dataIsValid = ref(true);

// The toolbar actions
const toolbarActions = [
    DropdownItemButton.create({
        buttonType: 'Primary',
        translationkey: 'general.addnew',
        title: 'general.addnewtitle',
        icon: 'fa-plus',
        action: () => {
            list.value?.addItem();
        },
    }),
    DropdownItemButton.create({
        buttonType: 'Delete',
        translationkey: 'general.delete',
        title: 'general.deletetitle',
        action: async () => {
            const selected = list.value?.getSelected() ?? [];

            const groupedByEntityState = arrayGroupbyToMap(selected, (item) => item.EntityState);

            // Delete those with state modified
            // Do we really need to ask to confirm?
            // Maybe we should confirm everything instead.
            if (groupedByEntityState.has('Modified')) {
                const confirmDeleteModified = await useDialog().YesNo({
                    title: 'stationalias.delete.confirm.withmodified.title',
                    body: 'stationalias.delete.confirm.withmodified.modified',
                    yesCallback: async () => {
                        groupedByEntityState.get('Modified')?.forEach((item) => {
                            item.Delete();
                        });
                        return true;
                    },
                });

                if (!confirmDeleteModified) {
                    return;
                }
            }

            // Delete the ones that are unchanged
            groupedByEntityState.get('Unchanged')?.forEach((item) => {
                item.Delete();
            });

            // Delete the ones that are added (these are just removed entirely)
            if (groupedByEntityState.has('Added')) {
                const addedThatShouldBeRemoved = groupedByEntityState.get('Added') ?? [];
                // Remove the items from data that are in the addedThatShouldBeRemoved
                data.value = data.value.filter((item) => !addedThatShouldBeRemoved.includes(item));
            }

            // We dont need to delete the ones that are already deleted

            // Tell the grid that we changed something (is this really needed)
            list.value?.dataUpdated();
        }, // We emit an empty array as we do not know which items are selected
    }),
];

const detailviewStatus = inject<Ref<ListCompState>>('ListCompState');

/**
 * Helper computed to check if the detail view is valid
 */
const isValidComputed = computed(() => {
    if (dataIsValid.value && detailviewStatus?.value != null) {
        // We return isValid = true to make sure we DONT show the alert message in the detail grid
        // It should be valid if there is ONLY negative integers in the x.FieldErrors
        // We need to iterate all the values of the map
        for (let fieldError of detailviewStatus?.value.InvalidFields.values() ?? []) {
            // We check if there is an
            let hasPositiveInteger = fieldError.some((code) => parseInt(code) >= 0);
            if (hasPositiveInteger) {
                return false;
            }
        }
    }
    return true;
});

/**
 * We expose some methods so we can use this as a component in a detail grid
 */
defineExpose<IGridDetailExposed<StationAlias>>({
    SaveGrid: () => {
        list.value?.saveList();
        return Promise.resolve();
    },

    IsChanged: computed(() => dataHasChanged.value),

    IsValid: isValidComputed,

    CanSave: computed(() => dataIsValid.value),
});
</script>
<template>
    <ContentComp
        class="h-100 p-0"
        :fill-content="true"
        :load-state="props.gridDetailView ? undefined : LoadState"
        @is-visible="isVisible"
    >
        <BaseObjListView
            class="p-0"
            ref="list"
            v-model="data"
            :baseType="StationAlias"
            :custom-columns="props.customColumns"
            :gui="useGuiStore().StationAlias"
            :hide-messages="true"
            :hide-statusbar="true"
            :hide-toolbar="true"
            :hide-search-field="true"
            :list-loader="refreshList"
            :loadOnMount="false"
            :resize-strategy="'sizeColumnsToFit'"
            :use-action-header="props.gridDetailView"
            @cell-key-down="onCellKeyDown"
            @datachanged="(changed: boolean) => (dataHasChanged = changed)"
            @item-added="itemAdded"
            @saving-items-end="savedList"
            @datastate="(valid: boolean) => (dataIsValid = valid)"
        >
            <ToolbarComp>
                <template #first>
                    <ButtonGroup :actions="toolbarActions" />
                </template>
            </ToolbarComp>
        </BaseObjListView>
    </ContentComp>
</template>
